/* eslint-disable jsx-a11y/no-autofocus */
import type { PopoverProps } from '@wix/design-system';
import { Box, Divider, SidePanel } from '@wix/design-system';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import { unreachable } from './../../../ricos-content/ts-utils';
import { RicosContext, ZIndexContext } from './../../../ricos-context';
import { SitePagesFetchContext } from './../../site-pages/site-pages-fetch-context';
import { Panel, PanelHeader } from './../../../ui-components';

import { MODAL_ROOT } from '../data-hooks/data-hooks';
import { AttributesDesktop } from './attributes/attributes-desktop';
import { Footer } from './footer';
import { NotPublishedWarning } from './not-published-warning';
import { SectionDesktop } from './section/section-desktop';
import { SitePages } from './site-pages/site-pages';
import { TypeSelect } from './type-select';
import type { LinkModalProps } from './types';
import { useLinkModalState } from './use-link-modal-state';
import { WebAddress } from './web-address';

export const ModalDesktop: React.FC<LinkModalProps> = ({
  initialValue,
  onClose,
  onSave,
  onUnlink,
  onSaveMultiple,
}) => {
  const { environment, t } = useContext(RicosContext);
  const { expireCache, preload, sitePublished } = useContext(SitePagesFetchContext);
  const zIndexService = useContext(ZIndexContext);

  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const {
    focusTarget,
    link,
    linkType,
    submitError,
    multipleSections,

    onChangeAttributes,
    onChangeWebAddress,
    onClickUnlink,
    onSelectPage,
    onSelectSection,
    setMultipleSections,
    onSelectType,
    onSubmit,
    onValidateWebAddress,
  } = useLinkModalState({ initialValue, onClose, onSave, onUnlink, onSaveMultiple });

  const autoCompletePopoverProps: PopoverProps = useMemo(
    () => ({
      appendTo: containerRef || undefined,
      placement: 'bottom-start',
      zIndex: zIndexService.getZIndex('DRAWER'),
      maxWidth: '252px',
    }),
    [containerRef, zIndexService]
  );

  const dropDownPopoverProps: PopoverProps = useMemo(
    () => ({
      appendTo: containerRef || undefined,
      maxWidth: '252px',
      zIndex: zIndexService.getZIndex('DRAWER'),
    }),
    [containerRef, zIndexService]
  );
  const tooltipProps = useMemo(
    () => ({
      appendTo: containerRef || undefined,
      fixed: true,
      zIndex: zIndexService.getZIndex('TOOLTIP'),
    }),
    [containerRef, zIndexService]
  );

  const showNotPublishedWarning =
    linkType !== 'section' && linkType !== 'web-address' && sitePublished === false;
  const showSitePages = environment === 'businessManager';

  useEffect(() => {
    if (showSitePages) {
      preload();
      return expireCache;
    }
  }, []);

  return (
    <div data-hook={MODAL_ROOT} ref={setContainerRef}>
      <Panel maxHeight={576} onCloseButtonClick={onClose}>
        <PanelHeader title={t('LinkSettings_Modal_Title')} />
        <SidePanel.Content noPadding>
          <form onSubmit={onSubmit}>
            <input type="submit" style={{ display: 'none' }} />
            <Box direction="vertical" gap="SP3" padding="SP3">
              <TypeSelect
                autoFocus={focusTarget === 'type-select'}
                dropDownPopoverProps={dropDownPopoverProps}
                onSelect={onSelectType}
                showSitePages={showSitePages}
                t={t}
                type={linkType}
              />
              {showNotPublishedWarning && <NotPublishedWarning />}
              {(() => {
                switch (linkType) {
                  case 'web-address':
                    return (
                      <WebAddress
                        autoFocus={focusTarget === 'input'}
                        key={linkType}
                        link={link}
                        onChangeUrl={onChangeWebAddress}
                        onValidateUrl={onValidateWebAddress}
                        submitError={submitError}
                        t={t}
                      />
                    );
                  case 'section':
                    return (
                      <SectionDesktop
                        autoCompletePopoverProps={autoCompletePopoverProps}
                        autoFocus={focusTarget === 'input'}
                        dropDownPopoverProps={dropDownPopoverProps}
                        key={linkType}
                        link={link}
                        onSelect={onSelectSection}
                        setMultipleSections={setMultipleSections}
                        onSubmit={onSubmit}
                        t={t}
                        tooltipProps={tooltipProps}
                        multipleSections={multipleSections}
                      />
                    );
                  case 'blogPost':
                  case 'staticPage':
                    return (
                      <SitePages
                        autoCompletePopoverProps={autoCompletePopoverProps}
                        autoFocus={focusTarget === 'input'}
                        key={linkType}
                        link={link}
                        linkType={linkType}
                        onSelect={onSelectPage}
                        onSubmit={onSubmit}
                        t={t}
                        tooltipProps={tooltipProps}
                      />
                    );
                  default:
                    unreachable(linkType);
                }
              })()}

              {linkType !== 'section' && (
                <>
                  <Divider />
                  <AttributesDesktop
                    link={link}
                    onChange={onChangeAttributes}
                    t={t}
                    tooltipProps={tooltipProps}
                  />
                </>
              )}
            </Box>
          </form>
        </SidePanel.Content>
        <SidePanel.Footer showDivider>
          <Footer
            onClickCancel={onClose}
            onClickSave={onSubmit}
            onClickUnlink={onClickUnlink}
            showUnlink={!!initialValue}
            t={t}
          />
        </SidePanel.Footer>
      </Panel>
    </div>
  );
};
